import {
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  OutlinedInput,
} from '@mui/material'
import './TextInput.scss'
import { useI18n } from '@ggs/i18n'

/**
 * @typedef {object} TextFieldProps
 * @property {string} id
 * @property {boolean=} fullWidth
 * @property {boolean=} error
 * @property {object=} formControlFields
 * @property {Object<string, string>=} formControlClasses
 * @property {string=} label
 * @property {Object<string, string>=} labelClasses
 * @property {string=} placeholder
 * @property {object=} inputProps
 * @property {object=} inputFields
 * @property {Object<string, string>=} inputClasses
 * @property {JSX.Element} startIcon
 * @property {JSX.Element} endIcon
 * @property {string=} helperText
 * @property {string=} errorHelperText
 * @property {object=} hel perTextFields
 * @property {Object<string, string>=} helperTextClasses
 */

/**
 * A component for an input field
 * @param {TextFieldProps} props
 * @return {JSX.Element}
 */
const TextInput = ({
  id,
  fullWidth = true,
  error = false,
  formControlFields = {},
  formControlClasses = {
    root: '',
  },
  label = 'Input field label text',
  labelClasses = {
    root: '',
    focused: '',
  },
  placeholder = 'Placeholder text',
  inputProps = {},
  inputFields = {},
  inputClasses = {
    root: '',
    focused: '',
    input: '',
    inputAdornedStart: '',
    inputAdornedEnd: '',
    notchedOutline: '',
  },
  startIcon = null,
  endIcon = null,
  helperText = '',
  errorHelperText = '',
  helperTextFields = {},
  helperTextClasses = {
    root: '',
  },
}) => {
  const { t } = useI18n()

  /**
   * @param {string} position
   * @return {JSX.Element}
   */
  const getIconComponent = (position) => {
    switch (position) {
      case 'start':
        return startIcon ? (
          <InputAdornment
            position="start"
            classes={{ root: 'a-input-control__icon a-input-control__icon--start' }}
            aria-hidden="true"
          >
            {startIcon}
          </InputAdornment>
        ) : null
      case 'end':
        return endIcon ? (
          <InputAdornment
            position="end"
            classes={{ root: 'a-input-control__icon a-input-control__icon--end' }}
            aria-hidden="true"
          >
            {endIcon}
          </InputAdornment>
        ) : null
      default:
        return null
    }
  }

  return (
    <FormControl
      {...formControlFields}
      fullWidth={fullWidth}
      error={error}
      classes={{
        ...formControlClasses,
        root: `a-input-control ${formControlClasses.root ?? ''}`.trim(),
      }}
    >
      <FormLabel
        htmlFor={id}
        classes={{
          ...labelClasses,
          root: `a-input-control__label ${labelClasses.root ?? ''}`.trim(),
          focused: `a-input-control__focused ${labelClasses.focused ?? ''}`.trim(),
        }}
      >
        {label}
      </FormLabel>
      <OutlinedInput
        id={id}
        placeholder={placeholder}
        aria-describedby={`${id}-helper-text`}
        inputProps={inputProps}
        classes={{
          ...inputClasses,
          root: `a-input-control__root ${inputClasses.root ?? ''}`.trim(),
          focused: `a-input-control__focused ${inputClasses.focused ?? ''}`.trim(),
          input: `a-input-control__input ${inputClasses.input ?? ''}`.trim(),
          inputAdornedStart: `a-input-control__input--adorned-start ${
            inputClasses.inputAdornedStart ?? ''
          }`.trim(),
          inputAdornedEnd: `a-input-control__input--adorned-end ${
            inputClasses.inputAdornedEnd ?? ''
          }`.trim(),
          notchedOutline: `a-input-control__no-outline ${inputClasses.notchedOutline ?? ''}`.trim(),
        }}
        startAdornment={getIconComponent('start')}
        endAdornment={getIconComponent('end')}
        {...inputFields}
      />
      <FormHelperText
        {...helperTextFields}
        id={`${id}-helper-text`}
        classes={{
          ...helperTextClasses,
          root: `a-input-control__helper-text ${helperTextClasses.root ?? ''}`.trim(),
        }}
      >
        {error ? errorHelperText || t('Error occurred') : helperText}
      </FormHelperText>
    </FormControl>
  )
}

export default TextInput
